import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import analyticsService from '../../../services/analyticsService';

const WelcomeBanner = ({ 
  userName = 'User',
  subscription = null,
  agentLimitStatus = null,
  onStartOnboarding,
  onUpgrade,
  className = ''
}) => {
  const currentHour = new Date()?.getHours();
  const greeting = currentHour < 12 ? 'Good Morning' : currentHour < 18 ? 'Good Afternoon' : 'Good Evening';

  const planName = subscription?.subscription_plans?.name || 'Free';
  const maxAgents = agentLimitStatus?.max_agents || subscription?.subscription_plans?.max_agents || 2;
  const activeAgents = agentLimitStatus?.active_agents || 0;
  const isFree = subscription?.tier === 'free';
  const isLimitReached = agentLimitStatus && !agentLimitStatus?.is_compliant;
  const hasGracePeriod = agentLimitStatus?.grace_period_end && new Date(agentLimitStatus?.grace_period_end) > new Date();
  const isExpired = subscription?.status === 'expired' || subscription?.status === 'canceled';

  const [gracePeriodCountdown, setGracePeriodCountdown] = useState('');

  useEffect(() => {
    if (!hasGracePeriod) return;

    const updateCountdown = () => {
      const now = new Date();
      const end = new Date(agentLimitStatus?.grace_period_end);
      const diff = end - now;

      if (diff <= 0) {
        setGracePeriodCountdown('Expired');
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      if (days > 0) {
        setGracePeriodCountdown(`${days}d ${hours}h remaining`);
      } else if (hours > 0) {
        setGracePeriodCountdown(`${hours}h ${minutes}m remaining`);
      } else {
        setGracePeriodCountdown(`${minutes}m remaining`);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [hasGracePeriod, agentLimitStatus?.grace_period_end]);

  const handleUpgradeClick = () => {
    // Track upgrade CTA click from dashboard banner
    analyticsService?.trackUpgradeCTA(
      'dashboard_banner',
      subscription?.tier || 'free'
    );
    
    if (onUpgrade) {
      onUpgrade();
    }
  };

  return (
    <div 
      className={`
        bg-gradient-to-br from-primary/10 via-primary/5 to-transparent
        rounded-xl border border-primary/20 p-6 md:p-8
        ${className}
      `}
    >
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Icon name="Sparkles" size={24} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-2xl md:text-3xl font-semibold text-foreground font-heading">
                {greeting}, {userName}!
              </h2>
              <p className="text-sm text-muted-foreground font-caption">
                {new Date()?.toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
            </div>
          </div>
          <p className="text-base md:text-lg text-foreground/80 leading-relaxed max-w-2xl mb-4">
            Your AI agents are ready to streamline your business operations. Monitor performance, manage tasks, and drive growth from your central command center.
          </p>
          
          {subscription && (
            <div className="flex items-center gap-4 flex-wrap">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-card/50 rounded-lg border border-border">
                <Icon name="CreditCard" size={16} className="text-primary" />
                <span className="text-sm font-medium text-foreground">{planName} Plan</span>
              </div>
              
              {agentLimitStatus && (
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border ${
                  isLimitReached 
                    ? 'bg-destructive/10 border-destructive/30' :'bg-card/50 border-border'
                }`}>
                  <Icon 
                    name="Users" 
                    size={16} 
                    className={isLimitReached ? 'text-destructive' : 'text-primary'} 
                  />
                  <span className={`text-sm font-medium ${
                    isLimitReached ? 'text-destructive' : 'text-foreground'
                  }`}>
                    {activeAgents}/{maxAgents} agents active
                  </span>
                </div>
              )}

              {hasGracePeriod && (
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-warning/10 rounded-lg border border-warning/30">
                  <Icon name="Clock" size={16} className="text-warning" />
                  <span className="text-sm font-medium text-warning">
                    Grace Period: {gracePeriodCountdown}
                  </span>
                </div>
              )}

              {isExpired && (
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-destructive/10 rounded-lg border border-destructive/30">
                  <Icon name="AlertCircle" size={16} className="text-destructive" />
                  <span className="text-sm font-medium text-destructive">
                    Subscription Expired
                  </span>
                </div>
              )}

              {(isFree || isLimitReached || isExpired) && (
                <button
                  onClick={handleUpgradeClick}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                >
                  <span>{isExpired ? 'Renew subscription' : isLimitReached ? 'Upgrade to add more agents' : 'Upgrade for more agents'}</span>
                  <Icon name="ArrowRight" size={14} />
                </button>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
          <Button
            variant="default"
            size="lg"
            onClick={onStartOnboarding}
            iconName="Rocket"
            iconPosition="left"
            className="w-full sm:w-auto"
          >
            Quick Setup
          </Button>
          {(isLimitReached || isExpired) && (
            <Button
              variant="outline"
              size="lg"
              onClick={handleUpgradeClick}
              iconName="ArrowUp"
              iconPosition="left"
              className="w-full sm:w-auto border-primary text-primary hover:bg-primary/10"
            >
              Upgrade Now
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default WelcomeBanner;